import React from 'react';

import ButtonCircular from '@deliveryhero/pd-cookbook/components/ButtonCircular';
import Button from '@deliveryhero/pd-cookbook/components/ButtonV2';
import IcHide from '@deliveryhero/pd-cookbook/components/Icons/IcHide';
import { Link } from '@deliveryhero/pd-cookbook/components/Link';
import { Message } from '@deliveryhero/pd-cookbook/components/Message';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import { InputAdornment } from '@mui/material';

import { TextField } from 'components/TextField';
import { Form, Formik, FormikHelpers } from 'formik';
import { AppRoute } from 'share/types/routes';

import { ILoginFormProps } from './types';
import { PasswordFormValidationSchema, PasswordFormValues } from './validation';

export const LoginForm = ({
  buttonText,
  initialValues,
  onSubmit,
  showPasswordInput = true,
  showEmailInput = true,
  isLoginPage = true
}: ILoginFormProps): JSX.Element => {
  const [hidePassword, setHidePassword] = React.useState(true);
  const [isSubmitDisabled, setIsSubmitDisabled] = React.useState(false);

  const displayErrorAlert = (message: string) => {
    return (
      <Message
        message={message}
        kind="error"
        style={{ marginBottom: '16px' }}
      />
    );
  };

  const handleSubmit = async (
    values: PasswordFormValues,
    actions: FormikHelpers<PasswordFormValues>
  ) => {
    await onSubmit(values).catch(() => setIsSubmitDisabled(true));
    actions.setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={PasswordFormValidationSchema}
    >
      {({ errors, touched, handleChange, values, isSubmitting }) => (
        <Form
          style={{
            width: '100%',
            textAlign: isLoginPage ? 'left' : 'center'
          }}
        >
          {errors.username &&
            errors.password &&
            displayErrorAlert('Email and password are both required fields')}
          {errors.username &&
            !errors.password &&
            displayErrorAlert(errors.username)}
          {!errors.username &&
            errors.password &&
            displayErrorAlert(errors.password)}
          {showEmailInput && (
            <TextField
              sx={{ pb: '16px' }}
              name="username"
              label="Work email"
              value={values.username}
              onChange={(e) => {
                handleChange(e);
                setIsSubmitDisabled(false);
              }}
              error={touched.username && errors.username ? true : false}
            />
          )}
          {showPasswordInput && (
            <TextField
              sx={{ pb: '16px' }}
              name="password"
              label="Password"
              value={values.password}
              onChange={(e) => {
                handleChange(e);
                setIsSubmitDisabled(false);
              }}
              type={hidePassword ? 'password' : 'text'}
              error={touched.password && errors.password ? true : false}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <ButtonCircular
                      size="small"
                      type="button"
                      greyIcon
                      role="PasswordIcon"
                      onClick={() => setHidePassword(!hidePassword)}
                    >
                      <IcHide fill="interaction-secondary" />
                    </ButtonCircular>
                  </InputAdornment>
                )
              }}
            />
          )}
          {isLoginPage && (
            <Typography type="label-md" marginBottom="sm">
              <Link href={AppRoute.ForgotPassword}>Forgot password</Link>
            </Typography>
          )}
          <Button
            type="submit"
            disabled={isSubmitDisabled}
            layout="full-width-primary"
            marginTop="md"
            status={isSubmitting ? 'is-loading' : 'is-idle'}
          >
            {buttonText}
          </Button>
          {!isLoginPage && (
            <Typography type="label-md" marginTop="md">
              <Link href={AppRoute.Login}>Back to login</Link>
            </Typography>
          )}
        </Form>
      )}
    </Formik>
  );
};
